import React, { useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { confirmPasswordReset } from "../api/authClient";

export default function ResetPasswordPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get("token");

    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [status, setStatus] = useState("idle");
    const [message, setMessage] = useState("");

    if (!token) {
        return (
            <div className="min-h-screen flex items-center justify-center px-4">
                <div className="bg-white border border-gray-200 rounded-2xl shadow-sm w-full max-w-md text-center p-8">
                    <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6">
                        <svg className="w-8 h-8 text-danger-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </div>
                    <h2 className="text-2xl font-bold text-danger-500 mb-3">Invalid Link</h2>
                    <p className="text-gray-500 mb-8">
                        No reset token provided. Please request a new password reset link.
                    </p>
                    <Link to="/forgot-password" className="btn-primary inline-flex">
                        Request New Link
                    </Link>
                </div>
            </div>
        );
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage("");

        if (password.length < 8) {
            setStatus("error");
            setMessage("Password must be at least 8 characters long.");
            return;
        }

        if (password !== confirmPassword) {
            setStatus("error");
            setMessage("Passwords do not match.");
            return;
        }

        setStatus("loading");

        try {
            await confirmPasswordReset(token, password);
            setStatus("success");
        } catch (err) {
            setStatus("error");
            const errorMessage = err.response?.data?.message || "Failed to reset password. The link may have expired.";
            setMessage(errorMessage);
        }
    };

    if (status === "success") {
        return (
            <div className="min-h-screen flex items-center justify-center px-4">
                <div className="bg-white border border-gray-200 rounded-2xl shadow-sm w-full max-w-md text-center p-8">
                    <div className="w-16 h-16 bg-accent-50 rounded-full flex items-center justify-center mx-auto mb-6">
                        <svg className="w-8 h-8 text-accent-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M5 13l4 4L19 7" />
                        </svg>
                    </div>
                    <h2 className="text-2xl font-bold text-accent-600 mb-3">Password Reset!</h2>
                    <p className="text-gray-500 mb-8">
                        Your password has been successfully updated.
                    </p>
                    <button onClick={() => navigate("/login")} className="btn-primary">
                        Continue to Login
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center px-4">
            <div className="w-full max-w-md">
                <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-8">
                    <div className="text-center mb-8">
                        <Link to="/" className="inline-flex items-center gap-3 mb-6">
                            <div className="w-10 h-10 bg-gray-900 rounded-xl flex items-center justify-center">
                                <span className="text-white font-bold">O</span>
                            </div>
                            <span className="text-xl font-semibold text-gray-900">OmniWorth</span>
                        </Link>
                        <h1 className="text-2xl font-bold text-gray-900">Set New Password</h1>
                        <p className="text-gray-500 mt-2">Enter your new password below.</p>
                    </div>

                    {status === "error" && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-sm text-danger-500">{message}</p>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                New Password
                            </label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                minLength={8}
                                className="input"
                                placeholder="••••••••"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Confirm Password
                            </label>
                            <input
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                                className="input"
                                placeholder="••••••••"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={status === "loading"}
                            className="btn-primary w-full"
                        >
                            {status === "loading" ? "Resetting..." : "Reset Password"}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}
